/**
 * 日本語. 모양은 ko.ts를 따른다 — 빠진 문구가 있으면 빌드가 실패한다.
 * 원어민 검토 전이다. 어색한 표현은 이 파일만 고치면 된다.
 */

import type { Messages } from './ko';

export const ja: Messages = {
  common: {
    back: '戻る',
    menu: 'メニュー',
    profile: 'プロフィール',
    mainNav: 'メイン',
    tripDates: '旅行の日付',
    save: '保存',
    add: '追加',
    delete: '削除',
    cancel: 'キャンセル',
    processing: '処理中…',
    failed: 'できませんでした',
    loading: '読み込み中…',
    beta: 'ベータ',
    tripNotFound: '旅行が見つかりません。',
    itemNotFound: '予定が見つかりません。',
    noInfo: '情報なし',
    querying: '検索中',
    dayN: (n) => `${n}日目`,
    dayWithDate: (n, date) => `${n}日目 · ${date}`,
  },

  tabs: { home: 'ホーム', schedule: '予定', map: '地図' },

  kind: {
    place: 'スポット',
    flight: '飛行機',
    train: '電車',
    bus: 'バス',
    ferry: '船',
  },

  transport: {
    walk: '徒歩',
    transit: '公共交通',
    car: '車',
  },

  carrierLabel: {
    place: '',
    flight: '便名',
    train: '列車',
    bus: 'バス路線',
    ferry: '航路 · 船名',
  },

  carrierPlaceholder: {
    place: '',
    flight: '例: JL091',
    train: '例: のぞみ21号',
    bus: '例: 空港リムジンバス',
    ferry: '例: 博多 → 釜山 クイーンビートル',
  },

  region: { KR: '韓国', GLOBAL: '海外' },

  leg: {
    late: (d: string) => `移動時間が足りません · ${d}遅れ`,
    transitMissing: '公共交通の情報なし',
    transitNoRoute: '路線なし',
    crossBorder: '国際区間 · 手入力',
    crossBorderShort: '国際区間',
    needsPlace: '場所を入れると自動計算',
    unknownTap: '移動情報なし · タップして入力',
    unknown: '移動情報なし',
    manual: '手入力',
  },

  signIn: {
    method: {
      google: 'Googleで続ける',
      apple: 'Appleで続ける',
      dev: '開発用アカウントで試す',
    },
    tagline: '友だちと一緒に作る旅行プラン',
    invited: '旅行に招待されました。ログインするとそのプランにすぐ入れます。',
    devNote:
      'まだバックエンドにつながっていないため、セッションは**このブラウザだけ**に残ります。招待と共同編集はSupabaseにつないでから使えます。',
    privacy: "ほかの人に見えるのはニックネームだけです。ログインに使ったメールアドレスは表示されません。",
    privacyLink: "プライバシーポリシー",
    features: [
      { title: 'みんなで編集', body: '友だちが変えた予定が再読み込みなしですぐ見えます。誰がいつ変えたかも残ります。' },
      { title: '地図と移動時間', body: '場所を入れると次の予定までの徒歩・公共交通の時間を計算します。韓国はカカオマップ、海外はGoogleマップ。' },
      { title: '家計簿・精算', body: '誰が払ったかを書くだけで、誰が誰にいくら送るかまで。通貨が混ざっても大丈夫。' },
    ],
    shotAlt: '1日の予定画面 — 予定の間に移動時間',
  },

  inApp: {
    opening: "いつものブラウザで開いています…",
    openedTitle: "いつものブラウザで開きました",
    openedBody: (app: string) => `${app}の中では Google ログインがブロックされるため、Safari や Chrome などのいつものブラウザで同じページを開きました。この画面は閉じて大丈夫です。開かなかった場合は下の方法で開いてください。`,
    backToKakao: "カカオトークに戻る",
    retry: "もう一度開く",
    manualTitle: (app: string) => `${app}の中ではログインできません`,
    manualBody: "Google はアプリ内ブラウザでのログインをブロックしています。Safari や Chrome などのいつものブラウザで開いてください。",
    steps: "画面の上か下にある ⋯ メニューから「Safari で開く」「ブラウザで開く」を選ぶか、リンクをコピーしてブラウザのアドレス欄に貼り付けてください。",
    linkLabel: "このページのアドレス",
    copyLink: "リンクをコピー",
    copied: "コピーしました — ブラウザに貼り付けてください",
    apps: { kakaotalk: "カカオトーク", line: "LINE", instagram: "Instagram", facebook: "Facebook", naver: "NAVER アプリ", band: "BAND", daum: "Daum アプリ", everytime: "Everytime", webview: "このアプリ" },
  },

  unavailable: {
    title: 'ただいまサービスを利用できません',
    body: 'メンテナンス中か、一時的な問題が発生しています。しばらくしてからもう一度お試しください。',
    retry: '再試行',
  },

  home: {
    title: 'ホーム',
    upcoming: 'これからの旅行',
    pastTrips: (n) => `過去の旅行 ${n}件`,
    stats: (items, members) => `予定 ${items}件 · ${members}人`,
    dayCount: (n) => `${n}日間`,
    bothRegions: '韓国 + 海外',
    dday: (n) => `あと${n}日`,
    departsToday: '今日出発',
    ongoing: '旅行中',
    past: '過去の旅行',
    newTrip: '新しい旅行を作る',
    joinByCode: '招待コードで参加',
  },

  guide: {
    title: '使い方',
    link: 'はじめての方へ — 使い方を見る',
    intro: '友だちと一緒に旅行プランを作るアプリです。旅行を作って友だちを招待すると、誰が書き換えてもみんなの画面にすぐ反映されます。',
    shotAlt: (title) => `${title}の画面`,
    steps: [
      {
        title: '旅行を作る',
        body: 'ホームの「新しい旅行を作る」から、名前・日付・表紙を選びます。海外なら基本のタイムゾーンをその都市に — 予定が現地時刻で表示されます。都市を移動する日は、予定画面で都市名をタップして変更します。',
      },
      {
        title: '友だちを招待',
        body: '予定画面の右上のメニュー(≡) →「メンバー・招待コード」から招待リンクをメッセンジャーで送ります。友だちがリンクを開いてログインすると、すぐ同じ旅行に参加できます。リンクがなければ、ホームの「招待コードで参加」にコードを入力します。',
      },
      {
        title: '予定を入れる',
        body: '日付を選んで「+ 予定を追加」。場所を検索すると結果が地図に番号付きのピンで表示され、どの店舗かを選べます。飛行機・電車・バスは種類を変えて出発・到着を入れます。',
      },
      {
        title: '1日の予定を見る',
        body: '予定の間の徒歩・公共交通・車の移動時間が自動で計算されます。区間をタップすると手段を変えたり時間を直接直したりできます。横にスワイプすると別の日へ移ります。',
      },
      {
        title: '地図で見る',
        body: '下の「地図」タブでその日のルートを地図で見られます。韓国国内はカカオマップ、海外はGoogleマップです。予定の詳細の「経路」を押すと、目的地が入った状態で地図アプリが開きます。',
      },
      {
        title: 'みんなで使う機能',
        body: 'メニュー(≡)には、家計簿(誰が払ったかを書けば精算まで)、行きたい場所(投票)、チェックリスト(持ち物)、変更履歴(誰が何を変えたか)、この日の予定を送る、があります。',
      },
      {
        title: '旅行中は',
        body: '旅行期間中はホームの一番上に今日のカードが出ます。今していること、次の予定までの時間、天気、次の場所への経路がひと目でわかります。',
      },
    ],
    tipsTitle: '知っておくと便利なこと',
    tips: [
      {
        q: 'この使い方をもう一度見るには',
        a: 'ホーム左上の ? ボタン、予定画面のメニュー(≡) →「使い方」、プロフィール下の「使い方」からいつでも開けます。',
      },
      {
        q: 'アプリのように使いたい',
        a: 'ホーム画面に追加してください。iPhoneはSafariの共有ボタン →「ホーム画面に追加」、AndroidはChromeのメニュー(⋮) →「ホーム画面に追加」(または「アプリをインストール」)。',
      },
      {
        q: 'メッセンジャーのリンクを押したら別のブラウザが開いた',
        a: '正常です。アプリ内ブラウザではGoogleログインが使えないため、SafariやChromeに移して開きます。',
      },
      {
        q: 'ネットがつながらないときは?',
        a: '一度開いた旅行はそのまま見られます。オフライン中の変更は端末に保存し、つながったら送信します。',
      },
      {
        q: '料金はかかりますか?',
        a: '無料で、広告もありません。ほかの人に見えるのはニックネームだけです。',
      },
    ],
    start: 'はじめる',
  },

  start: {
    title: '友だちと一緒に作る旅行プラン',
    body: '旅行を作って友だちを招待すると、予定・地図・家計簿を一緒に書き込めます。',
    step1: '旅行を作る — 名前と日付だけ',
    step2: '友だちを招待 — リンクかコードで',
    step3: '一緒に書き込む — 変更はすぐ反映',
    noTrip: 'まだ旅行がありません',
    scheduleBody: '旅行を作ると、日ごとに予定を書き込めます。',
    mapBody: '地図には旅行の予定の場所が日ごとに表示されます。',
  },

  today: {
    /** 지금 하고 있는 일정 — 오늘 카드와 일정 목록 */
    now: "いま",
    until: (end: string) => `${end}まで`,
    label: '今日',
    dayCity: (n, city) => `${n}日目 · ${city}`,
    localTime: (time) => `現地 ${time}`,
    next: '次の予定',
    inTime: (d) => `あと${d}`,
    allDone: '今日の予定はすべて終わりました。',
    empty: '今日はまだ予定がありません。',
    weather: {
      clear: '晴れ',
      cloudy: 'くもり',
      fog: '霧',
      rain: '雨',
      snow: '雪',
      storm: '雷雨',
    },
    rainChance: (n) => `今日の降水確率 ${n}%`,
    rate: (from, to) => `1 ${from} ≈ ${to}`,
  },

  trip: {
    title: '予定',
    notFound: '旅行が見つかりません。削除されたか、メンバーではなくなった可能性があります。',
    tzBanner: (delta, city) =>
      `前日との時差は**${delta}**です。以下の時刻はすべて**${city}の現地時刻**です。`,
    emptyDay: 'この日はまだ予定がありません。',
    addItem: '予定を追加',
    /** 일정 화면 위의 바로가기 — 메뉴 속 자주 쓰는 기능을 상태와 함께 */
    shortcuts: '旅行のショートカット',
    shortcutChecklist: (done: number, total: number) => total ? `持ち物 ${done}/${total}` : '持ち物',
    shortcutLedger: (n: number) => n ? `家計簿 ${n}件` : '家計簿',
    shortcutIdeas: (n: number) => n ? `候補 ${n}` : '候補地',
    menuChecklist: 'チェックリスト',
    timeConflict:
      '前の予定より早い時刻があります。日付をまたぐ予定でなければ、順番を確認してください。',
    timeConflictShort: '前の予定より早い時刻',
    sortByTime: '時刻順に並べる',
    menuReorder: '並べ替え',
    menuOptimize: 'ルート最適化',
    reorderDone: '完了',
    reorderHint: '右のハンドルをドラッグして並べ替えます。入力した時刻は変わりません。',
    reorderHandle: (title) => `${title}を移動`,
    menuLedger: '家計簿',
    menuIdeas: '行きたい場所',
    menuCalendar: 'カレンダーに書き出す',
    menuShareDay: 'この日の予定を送る',
    menuActivity: '変更履歴',
    lodgingFrom: (name: string) => `${name}から出発`,
    lodgingAt: (name: string) => `${name}に宿泊`,
    addLodging: 'この日の宿を決める',
    newBadge: 'NEW',
    newBadgeAria: (who: string) => `${who}さんが変更`,
    menuShare: '友だちに共有',
    menuMembers: 'メンバー · 招待コード',
    menuDelete: '旅行を削除',
    menuLeave: '旅行から抜ける',
    deleteTitle: '旅行を削除しますか？',
    deleteConfirm: '削除',
    deleteBody: (name) => `**${name}**の予定と持ち物がすべて削除されます。元に戻せません。`,
    deleteOthers: (n) => `一緒にいた${n}人もこの旅行を見られなくなります。`,
    leaveTitle: '旅行から抜けますか？',
    leaveConfirm: '抜ける',
    leaveBody: (name) =>
      `あなたのリストから**${name}**が消えます。予定はほかのメンバーにはそのまま残ります。`,
    leaveRejoin: 'また参加するには新しい招待リンクが必要です。',
  },

  optimize: {
    title: 'ルート最適化',
    distance: (before, after) => `移動距離 ${before} → ${after}`,
    saving: (pct) => `${pct}%短くなります(直線距離)`,
    noGain: '今の順番がすでに最短です。',
    note: '最初の予定、電車・飛行機などの区間、位置情報のない予定は動かしません。入力した時刻は変わりません。',
    noteLodging:
      '宿を出発・到着地点として計算しました。列車・飛行機などの区間と位置のない予定は動かさず、書いた時刻はそのままです。',
    timesOff: '⚠ 入力した時刻と順番が合わない予定が出ます。変更後に時刻を直してください。',
    apply: 'この順番にする',
    close: '閉じる',
    moved: '移動',
  },

  dayEdit: {
    label: '日付の設定',
    city: '都市',
    timezone: 'タイムゾーン',
    keepTimes: (city) =>
      `予定の時刻はそのままです。09:00の予定は${city}の現地時刻09:00になります。`,
    withFollowing: (n) => `続く${n}日間も一緒に変更`,
    until: (date, city) => `${date}まで · 現在 ${city}`,
    lodging: '宿泊先',
    lodgingPlaceholder: 'ホテル・宿を検索',
    lodgingHint: '最後の予定 → 宿、宿 → 翌日最初の予定の移動時間を計算します。',
  },

  tripCreate: {
    title: '新しい旅行',
    create: '作成',
    creating: '作成中…',
    cover: '表紙',
    coverAria: (emoji) => `表紙 ${emoji}`,
    name: '旅行の名前',
    namePlaceholder: '例: ソウル・釜山 5日間',
    start: '開始',
    end: '終了',
    timezone: '基本のタイムゾーン',
    timezoneHint:
      'すべての日付にこのタイムゾーンが付きます。別の都市へ移動する日は、予定画面で都市名をタップしてその日から変更してください。',
    dateCount: (n) => `作成される日付 ${n}日間`,
    submit: '旅行を作成',
    failed: '旅行を作成できませんでした',
  },

  itemEdit: {
    timeSuggested: (title: string) => `前の予定（${title}）が終わる時刻を入れました。移動時間の分だけ遅らせてください。`,
    deleteTitle: "この予定を削除しますか？",
    deleteBody: ["同じ旅行のメンバーの画面からも消えます。", "付いているコメントも一緒に削除され、元に戻せません。"],
    titleNew: '予定を追加',
    titleEdit: '予定を編集',
    date: '日付',
    dateMoveHint: 'その日の最後に移動します。時刻はそのままです。',
    kind: '種類',
    title: 'タイトル',
    titlePlaceholder: '例: ランチ · 冷麺',
    place: '場所',
    placePlaceholder: '場所を検索 (例: 明洞)',
    depart: '出発ターミナル',
    departPlaceholder: '例: ソウル駅、仁川空港',
    arrive: '到着ターミナル',
    arrivePlaceholder: '例: 釜山駅、済州港',
    time: '時刻',
    stay: '滞在時間',
    minutesSuffix: '分',
    bookingRef: '予約番号',
    bookingRefPlaceholder: '航空券・宿・ツアーの予約番号',
    memo: 'メモ',
    memoPlaceholder: '予約番号、持ち物、注意点…',
    defaultTitle: '新しい予定',
  },

  itemDetail: {
    stay: (d) => `滞在 ${d}`,
    howToMove: '移動手段',
    firstOfDay: 'この日の最初の予定です。前の区間はありません。',
    transitMissing:
      '公共交通の経路が見つかりませんでした。この地域にデータがないか、その時間帯に運行する路線がない可能性があります。徒歩と車だけ計算しました。Grabやトゥクトゥクなど現地の手段を使う場合は、下に直接入力してください。',
    crossBorder:
      '国境をまたぐ区間です。ルート検索では計算できないので、飛行機や列車の情報を直接入力してください。',
    moveTime: '移動時間',
    minus5: '5分減らす',
    plus5: '5分増やす',
    saveManual: '入力した時間で保存',
    manualNote: '手入力した値です。ルート検索の結果で上書きしません。',
    revertAuto: '自動計算に戻す',
    memo: 'メモ',
    noMemo: 'まだメモがありません。',
    drawnLine: '地図に描かれた線',
    viaTransit: '見つかった公共交通の路線',
    viaCar: '道路・航路に沿った車のルート',
    straight: 'ルートが見つからず、2つのターミナルを直線(点線)で結びました。',
    coordNote: '座標だけで探したルートです。',
    carrierDiffers: (code) => `入力した「${code}」とは異なる場合があります。`,
    edit: '編集',
    copy: 'コピー',
    copied: 'コピーしました',
  },

  edited: {
    leftMember: '抜けたメンバー',
    title: (name, when) => `${name === null ? 'あなた' : `${name}さん`}が編集${when ? ` · ${when}` : ''}`,
    line: (name) => `最終編集: ${name === null ? 'あなた' : `${name}さん`}`,
  },

  map: {
    title: '地図',
    noCoords: 'この日は位置情報のある予定がありません。',
    noTime: '時刻未定',
    startPoint: 'この日の出発地点です。',
    loading: '地図を読み込み中…',
    failed: '地図を読み込めませんでした',
    fallback: '地図を読み込めなかったため、簡易地図で表示しています。予定の順番とルートはそのままです。',
    schematic: '簡易地図',
    pinAria: (n, time, name) => `地点${n}: ${time ? `${time} ` : ''}${name}`,
  },

  place: {
    searchFailed: '場所が見つかりませんでした',
    searchHttpFailed: (status) =>
      `場所の検索に失敗しました (${status})。しばらくしてからもう一度お試しください。`,
    searching: '検索中…',
    noCoord: '座標がないため、地図には表示されません。',
    searchMore: '名所を名前でさらに探す',
    moreNone: 'これ以上見つかりませんでした。英語か現地の言葉で入力してみてください。',
    pinnedName: '地図で選んだ場所',
  },

  activity: {
    title: '変更履歴',
    needsDb: '変更履歴はまだ使えません。しばらくしてからもう一度開いてください。',
    offline: '接続が戻ったら見られます。',
    empty: 'まだ履歴がありません。予定を追加・変更するとここに残ります。',
    today: '今日',
    yesterday: '昨日',
    line: (
      who: string | null,
      target: 'item' | 'expense' | 'place',
      action: 'add' | 'update' | 'delete',
      title: string,
      day: number | null,
    ) => {
      const verb = {
        item: { add: 'を予定に追加しました', update: 'の予定を変更しました', delete: 'の予定を削除しました' },
        expense: { add: 'の支出を記録しました', update: 'の支出を変更しました', delete: 'の支出を削除しました' },
        place: { add: 'を候補に挙げました', update: 'の候補を変更しました', delete: 'の候補を削除しました' },
      }[target][action];
      return `${who ? `${who}さんが` : 'あなたが'}${day ? `${day}日目の` : ''}「${title}」${verb}`;
    },
  },

  shareDay: {
    heading: (emoji: string, trip: string, n: number, date: string, city: string) =>
      `${emoji} ${trip} · ${n}日目 ${date} · ${city}`,
    empty: 'まだ予定がありません。',
    copied: 'この日の予定をコピーしました — トークに貼り付けてください',
    failed: '送れませんでした。もう一度お試しください。',
  },

  directions: {
    open: '経路',
    kakao: 'カカオマップ',
    google: 'Googleマップ',
    aria: (app: string, place: string) => `${app}で${place}への経路`,
  },

  placeSearch: {
    region: '地域',
    domestic: '韓国国内',
    overseas: '海外',
    domesticPlaceholder: '例: 景福宮、ソウル駅',
    overseasPlaceholder: '例: 東京スカイツリー, Shinjuku',
    hint: '検索して選ぶか、地図を動かして中央のピンで指定してください',
    confirm: 'この場所にする',
    nameOnly: '場所なしで名前だけ入れる',
    mapUnavailable: '地図を読み込めませんでした。検索結果から選ぶか、名前だけ入れてください。',
    noResultsKr: '検索結果がありません。地図を動かして中央のピンで指定できます。',
    noResultsGlobal: '検索結果がありません。英語か現地の言葉で入力するか、地図を動かしてピンで指定してください。',
    clear: '場所を消す',
  },

  credits: {
    title: 'データの出典',
    searchUse: '場所検索 · ルート検索',
    osm: '© OpenStreetMap 貢献者',
    krSearchUse: '韓国国内の場所検索',
    kakao: 'Kakao',
    ratesUse: '為替レート',
    weatherUse: '天気',
    transitUse: '公共交通',
    transitous: 'Transitous (交通機関のGTFS)',
  },

  ledger: {
    title: '家計簿',
    needsDb: '家計簿はまだ使えません。しばらくしてからもう一度開いてください。',
    total: '支出合計',
    myShare: '自分の負担',
    settleIn: '精算通貨',
    settleTitle: '精算',
    allSettled: 'やり取りするお金はもうありません。',
    byCurrency: '為替レートを読み込めなかったため、通貨ごとに精算しました。',
    ratesAsOf: (date) => `為替レートは${date}時点です。`,
    empty: 'まだ支出がありません。誰が払ったかだけ入力すれば、精算はアプリがします。',
    add: '支出を追加',
    addTitle: '支出を追加',
    editTitle: '支出を編集',
    what: '何に使いましたか',
    whatPlaceholder: '例: 夕食 · ソムタム',
    amount: '金額',
    currency: '通貨',
    paidBy: '払った人',
    splitAmong: '割り勘する人',
    splitEach: (n, each) => `${n}人で${each}ずつ`,
    splitNone: '1人以上選んでください',
    date: '日付',
    noDate: '日付なし',
    paidLine: (name, n) => `${name}さんが支払い · ${n}人`,
    deleteTitle: 'この支出を削除しますか？',
    deleteBody: '精算も計算し直されます。',
    approx: (amount) => `≈ ${amount}`,
    sends: '送る人',
    receives: '受け取る人',
  },

  ideas: {
    title: '行きたい場所',
    needsDb: '行きたい場所はまだ使えません。しばらくしてからもう一度開いてください。',
    empty: '行きたい場所を追加して投票しましょう。票の多い場所から予定に入れれば大丈夫です。',
    add: '候補を追加',
    addTitle: '行きたい場所を追加',
    where: 'どこ',
    wherePlaceholder: '場所を検索 (例: 聖水洞カフェ通り)',
    note: 'ひとこと',
    notePlaceholder: '行きたい理由、値段、予約が必要か…',
    addedBy: (name) => `${name}さんが追加`,
    vote: '行きたい',
    voted: '投票済み',
    votes: (n) => `${n}票`,
    toPlan: '予定に入れる',
    toPlanTitle: 'どの日に入れますか？',
    toPlanHint: 'その日の予定の最後に入り、候補リストからは外れます。',
    removeTitle: (name) => `「${name}」を候補から削除しますか？`,
    removeBody: '投票も一緒に削除されます。',
  },

  comments: {
    title: 'コメント',
    placeholder: 'コメントする',
    send: '送信',
    empty: 'まだコメントがありません。',
    delete: '削除',
    count: (n) => `コメント ${n}件`,
  },

  checklist: {
    title: 'チェックリスト',
    empty: 'まだ項目がありません。',
    fromTemplate: '基本リストから選ぶ',
    templateTitle: '基本の持ち物',
    groupCommon: 'どこでも',
    groupAbroad: '海外旅行',
    already: '追加済み',
    addSome: (n: number) => (n > 0 ? `${n}件追加` : '選んだ項目がありません'),
    templates: {
      common: ['スマホの充電器', 'モバイルバッテリー', '身分証', 'カード・現金', '常備薬', '洗面用具', '着替え', '傘'],
      abroad: ['パスポート', 'パスポートのコピー', '航空券（eチケット）', '宿の予約確認', 'SIM・eSIM', '海外旅行保険', '両替', '海外で使えるカード', '変換プラグ'],
    },
    addPlaceholder: '項目を追加',
  },

  members: {
    title: 'メンバー',
    screenTitle: 'メンバー · 招待',
    code: '招待コード',
    codeAria: (spelled) => `招待コード ${spelled}`,
    sendLink: '招待リンクを送る',
    regenerate: '招待コードを変更',
    count: (n) => `メンバー ${n}人`,
    me: ' (自分)',
    owner: '作成者',
    kick: '外す',
    alone: 'まだひとりです。招待リンクを送れば、友だちがすぐに参加して一緒に編集できます。',
    kickTitle: (name) => `${name}さんを外しますか？`,
    kickBody: 'この旅行を見られなくなります。編集済みの予定はそのまま残ります。',
    kickBody2: '招待リンクを持っていれば再参加できます。防ぐには招待コードも変更してください。',
    regenTitle: '招待コードを変更しますか？',
    regenConfirm: '新しいコードを作成',
    regenBody: (code) => `これまでに送ったリンクとコード**${code}**では参加できなくなります。`,
    regenBody2: 'すでに参加しているメンバーはそのままです。',
  },

  invite: {
    title: '招待に参加',
    joining: '参加中…',
    code: '招待コード',
    placeholder: '例: 5G5D5UNX',
    hint: '大文字と小文字は区別しません。友だちからのリンクを開くと、この画面が自動で参加します。',
    join: '参加する',
    goHome: 'ホームへ',
    failed: '参加できませんでした',
  },

  share: {
    text: (name, code) => `${name}のプランを一緒に見よう (招待コード ${code})`,
    textNoUrl: (name, code) =>
      `${name}のプランを一緒に見よう\n招待コード: ${code}\nアプリで「招待コードで参加」をタップして入力してください。`,
    copiedLink: '招待リンクをコピーしました',
    copiedCode: '招待コードをコピーしました',
    codeToast: (code) => `招待コード: ${code}`,
  },

  profile: {
    contact: 'お問い合わせ・不具合の報告',
    contactSubject: '[TravelTomodachi] お問い合わせ',
    signOutPendingTitle: (n: number) => `まだ送っていない変更が ${n} 件あります`,
    signOutPendingBody: '今ログアウトすると、オフラインで変更した内容はサーバーに届かず失われます。インターネットにつないで送ってからログアウトしてください。',
    title: 'プロフィール',
    notSignedIn: 'ログインしていません。',
    via: {
      google: 'Googleアカウントでログイン中',
      apple: 'Appleアカウントでログイン中',
      dev: '開発用アカウントでログイン中',
    },
    nickname: 'ニックネーム',
    nicknamePlaceholder: '友だちに表示される名前',
    nicknameHint: (tag) =>
      `ほかの人と同じでも大丈夫です${tag ? `。後ろの番号(#${tag})で区別します` : ''}。いつでも変更できます。`,
    saved: '保存しました',
    saveNickname: 'ニックネームを保存',
    signOut: 'ログアウト',
    privacy: "ほかの人に見えるのはニックネームだけです。ログインに使ったメールアドレスは表示されません。",
    privacyLink: "プライバシーポリシー",
    defaultNickname: '旅行者',
    language: '言語',
    languageAuto: '端末の言語に合わせる',
    languageAutoHint: (name) => `現在の端末の言語: ${name}`,
    languageManualHint: '選んだ言語はこの端末でだけ使われます。',
    deleteAccount: "退会する",
    deleteTitle: "退会しますか？",
    deleteConfirm: "退会する",
    /** 탈퇴하면 무엇이 어떻게 되는지 — 확인 창에 한 줄씩 */
    deleteBody: ["ニックネームとログイン情報（メールアドレスなど）はすぐに削除され、元に戻せません。", "あなたが作った旅行は、いちばん先に参加したメンバーに引き継がれます。ひとりだけの旅行は削除されます。", "共有していた旅行に残した予定・コメント・支出は残り、作成者は「退出したメンバー」と表示されます。"],
    deleteFailed: "退会できませんでした",
  },

  privacyPage: {
    title: "プライバシーポリシー",
    effective: "施行日",
    /** 번역본이면 공식 문서가 한국어라는 안내. 한국어는 null */
    authoritative: "これは日本語訳です。正式なポリシーは韓国語版です。",
  },

  nicknameSetup: {
    title: '何と呼べばいいですか？',
    sub: '同じ旅行の友だちにこの名前で表示されます。',
    placeholder: '例: そら',
    hint: 'ほかの人と同じでも大丈夫です。プロフィールでいつでも変更できます。',
    invited: 'ニックネームを決めると、招待された旅行にすぐ入れます。',
    start: 'はじめる',
    later: 'あとで決める',
  },

  offline: {
    offline: 'オフライン — 最後に読み込んだ予定です。変更は接続が戻ったら送ります。',
    serverDown: 'サーバーに接続できないため、最後に読み込んだ予定を表示しています。',
    pending: (n: number) => `まだ送っていない変更が ${n} 件あります — 接続が戻ったら送ります`,
    syncing: '変更を送信中…',
    dropped: (n: number, reason: string) =>
      `オフラインでの変更のうち ${n} 件は保存できませんでした${reason ? `（${reason}）` : ''}。予定を確認してください。`,
  },

  nickname: {
    empty: 'ニックネームを入力してください',
    tooLong: (max) => `ニックネームは${max}文字までです`,
    hash: 'ニックネームに「#」は使えません',
  },

  errors: {
    signInFailed: 'ログインできませんでした',
    signInCancelled: 'ログインがキャンセルされました',
    signInNoCode: 'ログインの応答にコードがありません',
    unsupportedMethod: '対応していないログイン方法です',
    notSignedIn: 'ログインしていません',
    needSignIn: 'ログインが必要です',
    nicknameFailed: 'ニックネームを変更できませんでした',
    readProfile: 'プロフィールを読み込めませんでした',
    createProfile: 'プロフィールを作成できませんでした',
    saveFailed: '保存できませんでした',
    readTrips: '旅行を読み込めませんでした',
    readMembers: 'メンバーを読み込めませんでした',
    readDays: '日付を読み込めませんでした',
    readItems: '予定を読み込めませんでした',
    createTrip: '旅行を作成できませんでした',
    createDays: '日付を作成できませんでした',
    removeMember: 'メンバーを外せませんでした',
    removeMemberNone: '権限がないか、すでに抜けたメンバーです',
    deleteTrip: '旅行を削除できませんでした',
    deleteTripNotOwner: '旅行を作成した人だけが削除できます',
    createItem: '予定を追加できませんでした',
    updateItem: '予定を更新できませんでした',
    deleteItem: '予定を削除できませんでした',
    updateDays: '日付を更新できませんでした',
    addChecklist: '持ち物を追加できませんでした',
    updateChecklist: '持ち物を更新できませんでした',
    deleteChecklist: '持ち物を削除できませんでした',
    addExpense: '支出を追加できませんでした',
    updateExpense: '支出を更新できませんでした',
    deleteExpense: '支出を削除できませんでした',
    addPlace: '候補を追加できませんでした',
    removePlace: '候補を削除できませんでした',
    vote: '投票できませんでした',
    addComment: 'コメントを投稿できませんでした',
    deleteComment: 'コメントを削除できませんでした',
  },

  serverErrors: {
    needSignIn: 'ログインが必要です',
    codeNotFound: '招待コードが見つかりません',
    onlyOwnerRegenerate: '旅行を作成した人だけが招待コードを変更できます',
    regenerateFailed: '招待コードを作成できませんでした。もう一度お試しください',
    nicknameCrowded: 'このニックネームを使っている人が多すぎます。別のニックネームにしてください',
    tooMany: 'この旅行に追加できる数の上限に達しました',
    tooManyTrips: '作成できる旅行の数の上限に達しました',
  },
};
